import { cloneElement, isValidElement, useCallback, type MouseEvent, type ReactElement } from 'react'
import { CapWidget } from '@/lib/captcha/CapWidget'
import type { CapWidgetProps } from '@/lib/captcha/CapWidgetInner'
import {
  HUMAN_VERIFICATION_DEFAULTS,
} from '@/lib/captcha/human-verification'
import { useConfigStore } from '@/store/config'
import type { HumanVerificationScene } from '@/types/setting'

type CaptchaGateBaseProps = {
  scene: HumanVerificationScene
  onSolved: (token: string) => void | Promise<void>
  onBypass: () => void | Promise<void>
  onError?: (message: string) => void
  disabled?: boolean
}

type CaptchaGateInlineProps = CaptchaGateBaseProps &
  Omit<CapWidgetProps, 'onSolved' | 'onError' | 'mode'> & {
    mode?: 'inline'
  }

type CaptchaGateFloatingProps = CaptchaGateBaseProps &
  Omit<CapWidgetProps, 'onSolved' | 'onError' | 'mode' | 'trigger'> & {
    mode: 'floating'
    trigger: ReactElement
    floatingPosition?: 'top' | 'bottom'
    floatingOffset?: number
    onBeforeFloating?: () => boolean | Promise<boolean>
  }

export type CaptchaGateProps = CaptchaGateInlineProps | CaptchaGateFloatingProps

function useSceneEnabled(scene: HumanVerificationScene): boolean {
  const resolved = useConfigStore((state) => state.config?.is_human_verified)
  if (resolved) {
    return resolved[scene]
  }
  return HUMAN_VERIFICATION_DEFAULTS[scene]
}

type TriggerElement = ReactElement<{
  onClick?: (event: MouseEvent) => void
  disabled?: boolean
}>

export function CaptchaGate(props: CaptchaGateProps) {
  const { scene, onSolved, onBypass, onError, disabled } = props
  const enabled = useSceneEnabled(scene)

  const handleBypassClick = useCallback(() => {
    if (disabled) return
    void onBypass()
  }, [disabled, onBypass])

  if (!enabled) {
    if (props.mode === 'floating') {
      const { trigger } = props
      if (!isValidElement(trigger)) {
        throw new Error('CaptchaGate floating 模式的 trigger 必须是单个 React 元素')
      }
      const typedTrigger = trigger as TriggerElement
      return cloneElement(typedTrigger, {
        onClick: (event: MouseEvent) => {
          typedTrigger.props.onClick?.(event)
          if (!event.defaultPrevented) {
            handleBypassClick()
          }
        },
        disabled: disabled || typedTrigger.props.disabled,
      })
    }

    return null
  }

  if (props.mode === 'floating') {
    const { trigger, floatingPosition, floatingOffset, onBeforeFloating, ...rest } = props
    return (
      <CapWidget
        {...rest}
        mode="floating"
        trigger={trigger}
        floatingPosition={floatingPosition}
        floatingOffset={floatingOffset}
        onBeforeFloating={onBeforeFloating}
        disabled={disabled}
        onSolved={onSolved}
        onError={onError}
      />
    )
  }

  const { mode: _mode, ...rest } = props
  return (
    <CapWidget
      {...rest}
      mode="inline"
      disabled={disabled}
      onSolved={onSolved}
      onError={onError}
    />
  )
}
